/**
 * Generates data-driven (formulaic) intro copy + FAQ items for the category static SEO
 * header. Pure data-in -> text-out, same approach as generate-location-market-header-text.ts,
 * but deliberately avoids inline per-city Finnish/Swedish grammar declension (inessive cases
 * etc.) by presenting locations/categories as labelled lists rather than inline sentences —
 * that grammar table exists for locations already and isn't worth duplicating for category
 * copy where a list reads naturally in all three locales.
 */

import type { FaqItem } from '../../types/faq-item';

export type SupportedLocale = 'en' | 'fi' | 'sv';

export interface CategoryHeaderMetrics {
  categoryDisplayName: string;
  locale: SupportedLocale;
  jobCount: number;
  topLocations: Array<{ location: string; count: number }>;
  remotePercentage: number;
  relatedCategories: Array<{ slug: string; displayName: string }>;
  /** Not populated in phase 1 — no per-field salary aggregate exists yet. */
  avgSalaryFormatted?: string | null;
}

export interface CategoryHeaderText {
  introParagraph: string;
  faqItems: FaqItem[];
}

function localeNumber(value: number, locale: SupportedLocale): string {
  const localeString = locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-US';
  return Number.isFinite(value) ? value.toLocaleString(localeString) : String(value);
}

function topLocationNames(metrics: CategoryHeaderMetrics, limit = 3): string[] {
  return metrics.topLocations.slice(0, limit).map((l) => l.location.split(',')[0].trim());
}

function joinList(items: string[], locale: SupportedLocale): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  const and = locale === 'fi' ? 'ja' : locale === 'sv' ? 'och' : 'and';
  if (items.length === 2) return `${items[0]} ${and} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

function buildIntroEn(metrics: CategoryHeaderMetrics): string {
  const { categoryDisplayName, jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'en')} open ${categoryDisplayName} job${jobCount === 1 ? '' : 's'}` : `open ${categoryDisplayName} jobs`;
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Most positions are based in ${joinList(locations, 'en')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% offer remote or hybrid work.` : '';
  return `Browse ${jobsText} in Finland, updated daily from verified employer listings.${locationsPart}${remotePart}`;
}

function buildIntroFi(metrics: CategoryHeaderMetrics): string {
  const { categoryDisplayName, jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'fi')} avointa työpaikkaa` : 'avoimia työpaikkoja';
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Suosituimmat sijainnit: ${joinList(locations, 'fi')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% työpaikoista tarjoaa etä- tai hybridityötä.` : '';
  return `Selaa ${categoryDisplayName.toLowerCase()}-alan ${jobsText} Suomessa, päivittyy päivittäin.${locationsPart}${remotePart}`;
}

function buildIntroSv(metrics: CategoryHeaderMetrics): string {
  const { categoryDisplayName, jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'sv')} lediga jobb` : 'lediga jobb';
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Populäraste orterna: ${joinList(locations, 'sv')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% erbjuder distans- eller hybridarbete.` : '';
  return `Bläddra bland ${jobsText} inom ${categoryDisplayName} i Finland, uppdateras dagligen.${locationsPart}${remotePart}`;
}

function buildFaqEn(metrics: CategoryHeaderMetrics): FaqItem[] {
  const { categoryDisplayName, jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: `How many ${categoryDisplayName} jobs are available in Finland right now?`,
    answer: jobCount > 0
      ? `There are currently ${localeNumber(jobCount, 'en')} open ${categoryDisplayName} job${jobCount === 1 ? '' : 's'} listed on JobCrawls.`
      : `Open ${categoryDisplayName} positions are updated daily on JobCrawls.`,
  });

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: `Where are most ${categoryDisplayName} jobs located?`,
      answer: `Most ${categoryDisplayName} jobs in Finland are based in ${joinList(locations, 'en')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: `What percentage of ${categoryDisplayName} jobs are remote?`,
      answer: `${metrics.remotePercentage}% of ${categoryDisplayName} job listings offer remote or hybrid work arrangements.`,
    });
  }

  if (metrics.avgSalaryFormatted) {
    items.push({
      question: `What is the average salary for ${categoryDisplayName} roles?`,
      answer: `The average advertised salary for ${categoryDisplayName} roles is ${metrics.avgSalaryFormatted} per month.`,
    });
  }

  if (metrics.relatedCategories.length > 0) {
    items.push({
      question: `What related job categories should I also check?`,
      answer: `You may also be interested in ${joinList(metrics.relatedCategories.map((c) => c.displayName), 'en')} jobs.`,
    });
  }

  return items;
}

function buildFaqFi(metrics: CategoryHeaderMetrics): FaqItem[] {
  const { categoryDisplayName, jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: `Kuinka monta ${categoryDisplayName.toLowerCase()}-alan työpaikkaa Suomessa on avoinna juuri nyt?`,
    answer: jobCount > 0
      ? `JobCrawlsissa on tällä hetkellä ${localeNumber(jobCount, 'fi')} avointa ${categoryDisplayName.toLowerCase()}-alan työpaikkaa.`
      : `${categoryDisplayName}-alan avoimet työpaikat päivittyvät päivittäin JobCrawlsissa.`,
  });

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: `Missä ${categoryDisplayName.toLowerCase()}-alan työpaikkoja on eniten?`,
      answer: `Suurin osa ${categoryDisplayName.toLowerCase()}-alan työpaikoista sijaitsee: ${joinList(locations, 'fi')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: `Kuinka suuri osa ${categoryDisplayName.toLowerCase()}-alan työpaikoista on etätyötä?`,
      answer: `${metrics.remotePercentage}% ${categoryDisplayName.toLowerCase()}-alan työpaikoista tarjoaa etä- tai hybridityömahdollisuuden.`,
    });
  }

  if (metrics.avgSalaryFormatted) {
    items.push({
      question: `Mikä on ${categoryDisplayName.toLowerCase()}-alan keskipalkka?`,
      answer: `${categoryDisplayName}-alan ilmoitettu keskipalkka on ${metrics.avgSalaryFormatted} kuukaudessa.`,
    });
  }

  if (metrics.relatedCategories.length > 0) {
    items.push({
      question: `Mitä muita työkategorioita kannattaa myös katsoa?`,
      answer: `Saatat olla kiinnostunut myös näistä: ${joinList(metrics.relatedCategories.map((c) => c.displayName), 'fi')}.`,
    });
  }

  return items;
}

function buildFaqSv(metrics: CategoryHeaderMetrics): FaqItem[] {
  const { categoryDisplayName, jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: `Hur många ${categoryDisplayName}-jobb finns just nu i Finland?`,
    answer: jobCount > 0
      ? `Det finns för närvarande ${localeNumber(jobCount, 'sv')} lediga ${categoryDisplayName}-jobb på JobCrawls.`
      : `Lediga ${categoryDisplayName}-jobb uppdateras dagligen på JobCrawls.`,
  });

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: `Var finns de flesta ${categoryDisplayName}-jobben?`,
      answer: `De flesta ${categoryDisplayName}-jobben i Finland finns i: ${joinList(locations, 'sv')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: `Hur stor andel av ${categoryDisplayName}-jobben är distansarbete?`,
      answer: `${metrics.remotePercentage}% av ${categoryDisplayName}-jobben erbjuder distans- eller hybridarbete.`,
    });
  }

  if (metrics.avgSalaryFormatted) {
    items.push({
      question: `Vad är medellönen för ${categoryDisplayName}-roller?`,
      answer: `Den genomsnittliga annonserade lönen för ${categoryDisplayName}-roller är ${metrics.avgSalaryFormatted} per månad.`,
    });
  }

  if (metrics.relatedCategories.length > 0) {
    items.push({
      question: `Vilka andra jobbkategorier kan vara relevanta?`,
      answer: `Du kanske också är intresserad av: ${joinList(metrics.relatedCategories.map((c) => c.displayName), 'sv')}.`,
    });
  }

  return items;
}

export function generateCategoryMarketHeaderText(metrics: CategoryHeaderMetrics): CategoryHeaderText {
  switch (metrics.locale) {
    case 'fi':
      return { introParagraph: buildIntroFi(metrics), faqItems: buildFaqFi(metrics) };
    case 'sv':
      return { introParagraph: buildIntroSv(metrics), faqItems: buildFaqSv(metrics) };
    default:
      return { introParagraph: buildIntroEn(metrics), faqItems: buildFaqEn(metrics) };
  }
}
